import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { backendApi } from "@/utils/URL";
import { Button } from "@/components/Button";
import { useTranslation } from "react-i18next";

declare const __FRONTEND_VERSION__: string;
declare const __REACT_VERSION__: string;
declare const __TAILWIND_VERSION__: string;
declare const __NODE_VERSION__: string;

interface VersionData {
    php: string;
    laravel: string;
    mariadb: string | null;
    mysql: string | null;
    frontend?: string;
}

export const Versions: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation("global");
    const [data, setData] = useState<VersionData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchVersions = async () => {
            try {
                const base = backendApi() || "http://localhost:8000/api";
                const res = await fetch(`${base}/versions?json=1`, {
                    headers: { Accept: "application/json" },
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                setData(json);
            } catch {
                setError(t("versions.load_error"));
            } finally {
                setLoading(false);
            }
        };

        fetchVersions();
    }, []);

    return (
        <div className="flex min-h-screen items-center justify-center bg-black p-6">
            <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl dark:bg-zinc-900">
                <div className="mb-6 flex justify-center">
                    <img
                        src="/images/logo.png"
                        alt={t("app_title")}
                        className="h-10 object-contain"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = "/logo.png";
                        }}
                    />
                </div>

                <h1 className="mb-4 text-center text-xl font-bold text-zinc-900 dark:text-white">
                    {t("versions.title")}
                </h1>

                {loading ? (
                    <div className="py-8 text-center text-sm text-zinc-500">
                        {t("versions.loading")}
                    </div>
                ) : error ? (
                    <div className="py-4 text-center text-sm text-red-500">
                        {error}
                    </div>
                ) : (
                    <div className="space-y-3 rounded-lg p-4 text-sm text-zinc-700 dark:text-zinc-200">
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.frontend")}
                            </div>
                            <div className="font-semibold">
                                {__FRONTEND_VERSION__}
                            </div>
                        </div>
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.react")}
                            </div>
                            <div className="font-semibold">
                                {__REACT_VERSION__}
                            </div>
                        </div>
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.tailwind")}
                            </div>
                            <div className="font-semibold">
                                {__TAILWIND_VERSION__}
                            </div>
                        </div>
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.node")}
                            </div>
                            <div className="font-semibold">
                                {__NODE_VERSION__}
                            </div>
                        </div>
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.php")}
                            </div>
                            <div className="font-semibold">{data?.php}</div>
                        </div>
                        <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                            <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                {t("versions.laravel")}
                            </div>
                            <div className="font-semibold">
                                {data?.laravel}
                            </div>
                        </div>
                        {data?.mariadb && (
                            <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                                <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                    {t("versions.mariadb")}
                                </div>
                                <div className="font-semibold">
                                    {data?.mariadb}
                                </div>
                            </div>
                        )}
                        {data?.mysql && (
                            <div className="flex justify-between border-b border-zinc-200 pb-2 dark:border-zinc-700">
                                <div className="font-medium text-zinc-500 dark:text-zinc-400">
                                    {t("versions.mysql")}
                                </div>
                                <div className="font-semibold">
                                    {data?.mysql}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="mt-6 flex justify-center">
                    <Button variant="secondary" onClick={() => navigate(-1)}>
                        {t("common.back")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Versions;
